import { modalCtaStore } from '@gto/announcement-ui'

import { validateCtaLinkShape } from '../announcement-cta-link'
import { IMAGE_MAX_BYTES, validateMediaSize, VIDEO_MAX_BYTES } from '../announcement-media-limits'
import { parseBulletItems } from './block-drafts'
import {
  type BlockDraft,
  type BlockDraftId,
  BULLET_ITEM_MAX_LENGTH,
  BULLET_ITEMS_MAX,
  BULLET_ITEMS_MIN,
  CARD_HEADING_MAX_LENGTH,
  CARD_TEXT_MAX_LENGTH,
  CTA_BLOCK_LINK_MAX_LENGTH,
  CTA_BLOCK_TEXT_MAX_LENGTH,
  hasMediaSource,
  HEADING_HIGHLIGHT_MAX_LENGTH,
  HEADING_TEXT_MAX_LENGTH,
  type LeafBlockDraft,
  MEDIA_URL_MAX_LENGTH,
  type MediaBlockDraft,
  PARAGRAPH_HIGHLIGHT_MAX_LENGTH,
  PARAGRAPH_TEXT_MAX_LENGTH,
  ROW_CHILDREN_MAX,
  ROW_CHILDREN_MIN,
} from './block-types'

/**
 * The editable keys an error can attach to, across every block type. A union
 * rather than `string` so a message written against a field the block does not
 * have fails to compile.
 */
export type BlockFieldKey = 'text' | 'highlight' | 'itemsText' | 'url' | 'link' | 'heading'

/** Errors for ONE block: per-field, plus a `block`-level one a row can raise. */
export type BlockDraftError = Partial<Record<BlockFieldKey, string>> & { block?: string }

/**
 * Every block's errors, keyed by draft id — children included, flat.
 *
 * Flat rather than nested because a row's child is looked up by the same id the
 * child's own editor holds; nesting would make the lookup depend on knowing the
 * parent, which the leaf component deliberately does not.
 */
export type BlockDraftErrors = Record<BlockDraftId, BlockDraftError>

const isBlank = (value: string): boolean => value.trim() === ''

const tooLong = (value: string, max: number, label: string): string | undefined =>
  value.trim().length > max ? `${label} must be at most ${max} characters.` : undefined

/**
 * A hosted URL for an image or a video block.
 *
 * Deliberately the SAME shape rule the CTA link uses. The renderer puts this
 * straight into a `src`, so the values that silently render nothing are the same
 * ones — and an operator who pastes `cdn.gtowizard.com/promo.webp` has made the
 * identical mistake whether the field is called a link or a URL.
 */
const validateMediaUrl = (url: string, label: string): string | undefined =>
  tooLong(url, MEDIA_URL_MAX_LENGTH, label) ?? validateCtaLinkShape(url) ?? undefined

/**
 * The block's one asset, however it was attached.
 *
 * A picked file is checked against the API's own size cap here rather than only in
 * the picker: the message has to survive a save, since that is when the upload
 * would otherwise be pushed and refused with the bytes already paid for.
 */
const validateMediaBlock = (
  draft: MediaBlockDraft,
  label: string,
  maxBytes: number,
): string | undefined => {
  if (draft.file !== null) {
    return validateMediaSize(draft.file, maxBytes, label) ?? undefined
  }

  if (!hasMediaSource(draft)) {
    return `${label} needs a file or a URL.`
  }

  return draft.uploadId === '' ? validateMediaUrl(draft.url, label) : undefined
}

/**
 * Writes a message only when there IS one.
 *
 * Never assigning `undefined` is what lets every caller below decide on
 * `Object.keys(...).length`. Assigning it and filtering afterwards cannot be
 * typed honestly: `Object.entries` on a `Partial<Record<…>>` types its values as
 * `string`, so the filter that removes them reads to the compiler as a
 * comparison that can never be true.
 */
const assign = (error: BlockDraftError, key: BlockFieldKey, message: string | undefined): void => {
  if (message !== undefined) {
    error[key] = message
  }
}

/**
 * A phrase is painted by finding it inside the text, so one that is not in the
 * text renders nothing at all — the same silent nothing the flat
 * `title_highlight` rule catches.
 */
const validateHighlight = (
  text: string,
  highlight: string,
  max: number,
  noun: string,
): string | undefined =>
  tooLong(highlight, max, 'Highlight') ??
  (text.includes(highlight.trim())
    ? undefined
    : `The highlight must appear in the ${noun} exactly, or nothing is painted.`)

const validateLeaf = (draft: LeafBlockDraft): BlockDraftError => {
  const error: BlockDraftError = {}

  switch (draft.type) {
    case 'heading': {
      assign(
        error,
        'text',
        isBlank(draft.text)
          ? 'A heading needs text.'
          : tooLong(draft.text, HEADING_TEXT_MAX_LENGTH, 'Heading'),
      )

      if (!isBlank(draft.highlight)) {
        assign(
          error,
          'highlight',
          validateHighlight(draft.text, draft.highlight, HEADING_HIGHLIGHT_MAX_LENGTH, 'heading'),
        )
      }
      break
    }
    case 'paragraph': {
      assign(
        error,
        'text',
        isBlank(draft.text)
          ? 'A paragraph needs text.'
          : tooLong(draft.text, PARAGRAPH_TEXT_MAX_LENGTH, 'Paragraph'),
      )

      const highlight = draft.highlight ?? ''
      if (!isBlank(highlight)) {
        assign(
          error,
          'highlight',
          validateHighlight(draft.text, highlight, PARAGRAPH_HIGHLIGHT_MAX_LENGTH, 'paragraph'),
        )
      }
      break
    }
    case 'hero':
      assign(error, 'url', validateMediaBlock(draft, 'Hero image', IMAGE_MAX_BYTES))
      break

    case 'bullets': {
      const items = parseBulletItems(draft.itemsText)
      if (items.length < BULLET_ITEMS_MIN) {
        error.itemsText = 'A bullet list needs at least one item.'
      } else if (items.length > BULLET_ITEMS_MAX) {
        error.itemsText = `A bullet list holds at most ${BULLET_ITEMS_MAX} items.`
      } else if (items.some((item) => item.length > BULLET_ITEM_MAX_LENGTH)) {
        error.itemsText = `Each bullet must be at most ${BULLET_ITEM_MAX_LENGTH} characters.`
      }

      break
    }
    case 'image':
      assign(error, 'url', validateMediaBlock(draft, 'Image', IMAGE_MAX_BYTES))
      break
    case 'video':
      assign(error, 'url', validateMediaBlock(draft, 'Video', VIDEO_MAX_BYTES))
      break
    case 'cta':
      assign(
        error,
        'text',
        // A store badge's artwork carries its own words, so its label is
        // optional: the modal names it after the store when the label is blank.
        isBlank(draft.text) && modalCtaStore(draft.style, draft.link) === null
          ? 'A button needs a label.'
          : tooLong(draft.text, CTA_BLOCK_TEXT_MAX_LENGTH, 'Label'),
      )
      // Presence first, so a missing link reads as missing rather than malformed.
      assign(
        error,
        'link',
        isBlank(draft.link)
          ? 'A button needs a link.'
          : (tooLong(draft.link, CTA_BLOCK_LINK_MAX_LENGTH, 'Link') ??
              validateCtaLinkShape(draft.link) ??
              undefined),
      )
      break
    case 'card':
      assign(
        error,
        'heading',
        isBlank(draft.heading)
          ? 'A card needs a heading.'
          : tooLong(draft.heading, CARD_HEADING_MAX_LENGTH, 'Heading'),
      )
      // Text is optional: a one-line card (heading only) is a valid brief shape.
      assign(error, 'text', tooLong(draft.text, CARD_TEXT_MAX_LENGTH, 'Card text'))
      break
  }

  return error
}

/**
 * Every block's errors, in one pure pass over the drafts.
 *
 * Pure and cheap on purpose: the form calls it to decide whether the save is
 * blocked, and the editor calls it to render each message, so the two can never
 * disagree about what is wrong. Nothing here is stored in state — a fixed field
 * clears its own error on the next keystroke rather than on the next submit.
 */
export const validateBlockDrafts = (drafts: readonly BlockDraft[]): BlockDraftErrors => {
  const errors: BlockDraftErrors = {}

  for (const draft of drafts) {
    if (draft.type === 'row') {
      // The contract types `children` as 2..3 items; the editor's own add and
      // remove controls stop short of both bounds, so this fires only for a row
      // built before those bounds existed.
      const count = draft.children.length
      if (count < ROW_CHILDREN_MIN || count > ROW_CHILDREN_MAX) {
        errors[draft.id] = {
          block: `A row holds ${ROW_CHILDREN_MIN} or ${ROW_CHILDREN_MAX} blocks side by side.`,
        }
      } else if (draft.children.every((child) => child.type === 'empty')) {
        errors[draft.id] = { block: 'A row needs at least one content block.' }
      }

      for (const child of draft.children) {
        if (child.type === 'empty') {
          continue
        }
        const childError = validateLeaf(child)
        if (Object.keys(childError).length > 0) {
          errors[child.id] = childError
        }
      }

      continue
    }

    const error = validateLeaf(draft)
    if (Object.keys(error).length > 0) {
      errors[draft.id] = error
    }
  }

  return errors
}

/**
 * The one line the form shows against the Content section, or undefined when the
 * blocks are clean.
 *
 * A count rather than the first message: the errors render on the blocks
 * themselves, where the operator can act on them, and repeating one of them at
 * the top would point at a field that may be several blocks away.
 */
export const blocksSummaryError = (drafts: readonly BlockDraft[]): string | undefined => {
  const count = Object.keys(validateBlockDrafts(drafts)).length

  if (count === 0) {
    return undefined
  }

  return count === 1 ? '1 block needs fixing.' : `${count} blocks need fixing.`
}
